import { Button, Checkbox, Form, Image, Input, Menu, message } from 'antd';
import Title from 'antd/lib/typography/Title';
import { EditOutlined, LogoutOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import BookyBox from '../../Components/Layout/BookyBox';
import PagePublicSite from '../../Components/Page/PagePublicSite';
import { BookyPhoneInput } from '../../Components/Forms/BookyPhoneInput';
import NotFound from '../../Components/Page/NotFoundPage';
import { TextService } from '../../Services/TextService';
import { getCurrentLanguage } from '../../Helpers/RouteHelper';
import { PublicSiteService } from '../../Services/PublicSiteService';
import { WebApiCompanyDtoCompanyPublicSiteDto } from '../../Repository/eventbookyapi';
import { EmailNotificationPreference } from '../../Types/EmailNotificationPreference';
import { PublicResultType } from './Types/PublicResultType';

enum State {
    Loading,
    CompanyNotFound,
    MemberFound
}

/**
 * Public-site self-edit page for a household family member. Shows only the family-member field
 * set (name, email, phone, email-notification preference) - no address, member number, custom
 * fields, groups or payment status. Reached via the `/editmember` landing dispatcher when the
 * logged-in member's role is FamilyMember.
 */
const PublicSiteEditFamilyMember = () => {
    const params = useParams();
    const navigation = useNavigate();
    const [form] = Form.useForm();
    const GetText = TextService.GetText;

    const [state, setState] = useState(State.Loading);
    const [publicSite, setPublicSite] = useState({} as WebApiCompanyDtoCompanyPublicSiteDto);

    useEffect(() => {
        const loadData = async () => {
            if (!params.companyurl) {
                setState(State.CompanyNotFound);
                return;
            }

            try {
                const company = await PublicSiteService.GetCompanyPublicSiteByUrl(params.companyurl);
                setPublicSite(company);

                const member = await PublicSiteService.GetFamilyMemberSelf();
                form.setFieldsValue({
                    firstName: member.firstName ?? '',
                    lastName: member.lastName ?? '',
                    email: member.email ?? '',
                    phoneNumber: member.phoneNumber ?? '',
                    noEmail: member.emailNotificationPreference === EmailNotificationPreference.NoInformationEmails,
                });
                setState(State.MemberFound);
            } catch {
                setState(State.CompanyNotFound);
            }
        };

        loadData();
    }, [params.companyurl]);

    const getDownloadUrl = (dto: WebApiCompanyDtoCompanyPublicSiteDto) => {
        return process.env.REACT_APP_API_URL + '/Image/downloadImage?bloburl=' + dto.siteLogoPath;
    };

    const onPhoneChange = (value: any) => {
        form.setFieldValue('phoneNumber', value);
    };

    const onSave = async (values: any) => {
        let result;
        try {
            result = await PublicSiteService.UpdateFamilyMemberSelf({
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                phoneNumber: values.phoneNumber,
                emailNotificationPreference: values.noEmail
                    ? EmailNotificationPreference.NoInformationEmails
                    : EmailNotificationPreference.Unspecified,
            });
        } catch {
            message.warning(GetText('PublicSiteEditMember_Error'));
            return;
        }

        const successType = result?.emailChangePending
            ? PublicResultType.EmailChangeConfirmationSent
            : PublicResultType.DetailsHasBeenSaved;
        navigation('/' + getCurrentLanguage() + '/' + params.companyurl + '/success/' + successType);
    };

    const onLogout = async () => {
        try {
            await PublicSiteService.Logout();
        } catch {
            // ignore
        } finally {
            navigation('/' + getCurrentLanguage() + '/' + params.companyurl + '/login');
            window.location.reload();
        }
    };

    if (state === State.CompanyNotFound) {
        return <NotFound message={GetText('PublicSiteEditMember_CompanyNotFound')} />;
    }

    return (
        <PagePublicSite>
            {state === State.MemberFound && (
                <>
                    {publicSite.siteLogoPath ? (
                        <div style={{ marginBottom: '20px', textAlign: 'center' }}>
                            <Image preview={false} className='company-logo' src={getDownloadUrl(publicSite)} />
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
                            <Title style={{ margin: 0 }}>{publicSite.siteTitle}</Title>
                        </div>
                    )}
                    <div style={{ maxWidth: '960px', margin: '0 auto', padding: '0 20px', marginBottom: '24px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '2px solid #f0f0f0' }}>
                            <Menu
                                mode='horizontal'
                                selectedKeys={['edit']}
                                style={{ border: 'none', flex: 1 }}
                                overflowedIndicator={null}
                                items={[{ key: 'edit', icon: <EditOutlined />, label: GetText('PublicSiteMenu_EditProfile') }]}
                            />
                            <Menu
                                mode='horizontal'
                                style={{ border: 'none' }}
                                overflowedIndicator={null}
                                items={[{ key: 'logout', icon: <LogoutOutlined />, label: GetText('PublicSiteMenu_Logout'), onClick: onLogout }]}
                            />
                        </div>
                    </div>
                </>
            )}
            <BookyBox>
                {state === State.MemberFound && <h2>{GetText('PublicSiteEditMemberEditMemberTitle')}</h2>}

                {state === State.MemberFound &&
                    publicSite.siteDescription &&
                    publicSite.siteDescription.length > 0 ? (
                    <p dangerouslySetInnerHTML={{ __html: publicSite.siteDescription! }}></p>
                ) : ''}

                {state === State.MemberFound && (
                    <Form
                        data-testid='family-self-edit-form'
                        form={form}
                        colon={false}
                        onFinish={onSave}
                        layout='vertical'
                        autoComplete='off'
                    >
                        <Form.Item name='firstName' label={GetText('MemberList_FirstNameColumn')} rules={[{ required: true }]}>
                            <Input data-testid='family-firstName' />
                        </Form.Item>
                        <Form.Item name='lastName' label={GetText('MemberList_LastNameColumn')}>
                            <Input data-testid='family-lastName' />
                        </Form.Item>
                        <Form.Item name='email' label={GetText('MemberList_EmailColumn')} rules={[{ type: 'email' }]}>
                            <Input data-testid='family-email' />
                        </Form.Item>
                        <BookyPhoneInput
                            label={GetText('MemberList_PhoneColumn')}
                            name='phoneNumber'
                            onchange={onPhoneChange}
                            placeholder={''}
                        />
                        <Form.Item name='noEmail' valuePropName='checked'>
                            <Checkbox data-testid='family-noEmail'>{GetText('MemberList_FamilyEmailOptOut')}</Checkbox>
                        </Form.Item>

                        <Form.Item>
                            <Button data-testid='submit-button' className='saveButton' size='middle' type='primary' htmlType='submit'>
                                {GetText('EditMember_ModalOk')}
                            </Button>
                        </Form.Item>
                    </Form>
                )}
            </BookyBox>
        </PagePublicSite>
    );
};

export default PublicSiteEditFamilyMember;
